// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormattedTimeValue } from '@/components/formatted-time-value';
import { CustomerTriage } from '@/customer/api/triage-types';
import {
  Box,
  Button,
  Chip,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Link } from '@tanstack/react-router';

interface CustomerTriageTableRowProps {
  triage: CustomerTriage;
  onClassifyClick: (triage: CustomerTriage) => void;
  onDismissClick: (triage: CustomerTriage) => void;
}

export function CustomerTriageTableRow(props: CustomerTriageTableRowProps) {
  const { triage, onClassifyClick, onDismissClick } = props;

  const isMatched = triage.isTriaged;
  const isDismissed = triage.isDismissed;

  const getStatusChip = () => {
    if (isDismissed) {
      return { color: 'default' as const, label: 'Dismissed' };
    }
    if (isMatched) {
      return { color: 'success' as const, label: 'Matched' };
    }
    return { color: 'warning' as const, label: 'Needs review' };
  };

  const statusChip = getStatusChip();

  const renderAction = () => {
    if (isMatched && triage.customer) {
      return (
        <Link
          to="/customers/$customerKey"
          params={{ customerKey: triage.customer.key }}
        >
          <Button size="small" variant="text" sx={{ whiteSpace: 'nowrap' }}>
            View {triage.customer.name}
          </Button>
        </Link>
      );
    }

    if (isDismissed) {
      return (
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={() => onClassifyClick(triage)}
        >
          Classify
        </Button>
      );
    }

    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={() => onDismissClick(triage)}
        >
          Dismiss
        </Button>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={() => onClassifyClick(triage)}
        >
          Classify
        </Button>
      </Box>
    );
  };

  return (
    <TableRow hover sx={{ '& td': { verticalAlign: 'middle' } }}>
      <TableCell>
        <Typography variant="body2">{triage.displayName}</Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color={triage.companyName ? 'text.primary' : 'text.disabled'}
        >
          {triage.companyName || '\u2014'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color={triage.domain ? 'text.primary' : 'text.disabled'}
        >
          {triage.domain || '\u2014'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color={triage.email ? 'text.primary' : 'text.disabled'}
        >
          {triage.email || '\u2014'}
        </Typography>
      </TableCell>
      <TableCell>
        <FormattedTimeValue timestamp={triage.createdAt} />
      </TableCell>
      <TableCell>
        <Chip
          label={statusChip.label}
          color={statusChip.color}
          size="small"
          variant="filled"
        />
      </TableCell>
      <TableCell align="right">{renderAction()}</TableCell>
    </TableRow>
  );
}
